import { decamelize, camelize } from 'humps';
import { getUserApiClient } from 'lib/SplashApiClient/subclients/UserApiClient';
import { getIsUserUsingRoleBasedPermissions } from 'common/store/userProperties/selectors';
import Logger from 'observability/Logger';
import { APP } from '../index';

const LOG = new Logger('userPermissions');

// Constants
export const USER_PERMISSIONS = 'userPermissions';

export const SET_USER_PERMISSIONS = `${APP}/SET_USER_PERMISSIONS`;
export const FETCH_PERMISSIONS_REQUEST = `${APP}/FETCH_PERMISSIONS_REQUEST`;
export const FETCH_PERMISSIONS_SUCCESS = `${APP}/FETCH_PERMISSIONS_SUCCESS`;
export const FETCH_PERMISSIONS_ERROR = `${APP}/FETCH_PERMISSIONS_ERROR`;

// Reducers
export const INIT_STATE = {
    [USER_PERMISSIONS]: {},
};

export const reducers = {
    [SET_USER_PERMISSIONS](state, { permissionCodes }) {
        if (!permissionCodes) return state;

        const transformedCodes = Object.entries(permissionCodes).reduce(
            /* Turn the passed values into the appropriate shape */
            (codeCollection, [code, access]) => ({
                ...codeCollection,
                [camelize(code)]: {
                    access,
                    isLoading: false,
                },
            }),
            {},
        );

        return {
            ...state,
            [USER_PERMISSIONS]: {
                ...state[USER_PERMISSIONS],
                ...transformedCodes,
            },
        };
    },
    [FETCH_PERMISSIONS_REQUEST](state, { permissionCodes }) {
        if (!permissionCodes) return state;

        const update = permissionCodes.reduce(
            (codeCollection, code) => ({
                ...codeCollection,
                [camelize(code)]: {
                    access: false,
                    isLoading: true,
                },
            }),
            {},
        );

        return {
            ...state,
            [USER_PERMISSIONS]: {
                ...state[USER_PERMISSIONS],
                ...update,
            },
        };
    },
    [FETCH_PERMISSIONS_SUCCESS](state, { permissions }) {
        const update = Object.entries(permissions).reduce(
            (codeCollection, [code, value]) => ({
                ...codeCollection,
                [camelize(code)]: {
                    access: value,
                    isLoading: false,
                },
            }),
            {},
        );
        return {
            ...state,
            [USER_PERMISSIONS]: {
                ...state[USER_PERMISSIONS],
                ...update,
            },
        };
    },
    [FETCH_PERMISSIONS_ERROR](state, { permissionCodes }) {
        if (!permissionCodes) return state;

        const update = permissionCodes.reduce(
            (codeCollection, code) => ({
                ...codeCollection,
                [camelize(code)]: {
                    access: false,
                    isLoading: false,
                },
            }),
            {},
        );

        return {
            ...state,
            [USER_PERMISSIONS]: {
                ...state[USER_PERMISSIONS],
                ...update,
            },
        };
    },
};

// Actions

/**
 *  Set multiple permissions
 *
 *  Payload should be in the structure of
 *  {
 *      [permissionName]: true|false
 *  }
 *
 *  @param {object}  permissionCodes
 */
export const setUserPermissions = (permissionCodes) => ({
    type: SET_USER_PERMISSIONS,
    permissionCodes,
});

/**
 * Fetch the value of multiple permissions
 *
 *  @param {array} permissionCodes
 */
export const fetchPermissions = (permissionCodes) => (dispatch, getState) => {
    const isUserUsingRoleBasedPermissions = getIsUserUsingRoleBasedPermissions(getState());
    if (!isUserUsingRoleBasedPermissions) {
        return null;
    }
    dispatch({ type: FETCH_PERMISSIONS_REQUEST, permissionCodes });
    return getUserApiClient()
        .fetchUserPermissions(permissionCodes.map((code) => decamelize(code)))
        .then(
            ({ data }) => {
                const { permissions } = data;
                return dispatch({
                    type: FETCH_PERMISSIONS_SUCCESS,
                    permissions,
                });
            },
            (err) => {
                LOG.error('Error on fetchPermissions', err);
                dispatch({ type: FETCH_PERMISSIONS_ERROR, permissionCodes });
            },
        );
};

// App module
export default {
    initialState: INIT_STATE,
    reducers,
};
